import {
  CITATION_DELIMITER,
  CITATION_START,
  CITATION_STOP,
  stripCitationTokens,
} from "./parseCitations";

interface HastNode {
  type?: string;
  value?: unknown;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

function sanitizeProperty(value: unknown): unknown {
  if (typeof value === "string") return stripCitationTokens(value);
  if (Array.isArray(value)) return value.map(sanitizeProperty);
  return value;
}

/**
 * Sanitize after Markdown/entity decoding. Raw-input cleanup alone is not a
 * trust boundary because numeric HTML entities can decode into private citation
 * sentinels while remark builds the tree.
 */
export function rehypeSanitizeCitationTokens() {
  return (tree: HastNode): void => {
    let frameDepth = 0;

    // Depth persists across text nodes so an entity-decoded frame split by
    // emphasis/link markup cannot leak the middle of its private payload.
    const sanitizeText = (text: string): string => {
      let clean = "";
      for (const character of text) {
        if (character === CITATION_START) {
          frameDepth += 1;
        } else if (character === CITATION_STOP) {
          if (frameDepth > 0) frameDepth -= 1;
        } else if (character !== CITATION_DELIMITER && frameDepth === 0) {
          clean += character;
        }
      }
      return clean;
    };

    const visit = (node: HastNode): void => {
      if (node.type === "text" && typeof node.value === "string") {
        node.value = sanitizeText(node.value);
      }
      if (node.properties) {
        for (const [key, value] of Object.entries(node.properties)) {
          node.properties[key] = sanitizeProperty(value);
        }
      }
      node.children?.forEach(visit);
    };

    visit(tree);
  };
}
